import { QIcon_chevron_bold_right } from '@repo/icons/index'
import classNames from 'classnames'
import { debounce } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useEventTracking } from '../../helpers/gtmHelper'
import { useBandBannerData } from '../../modules/apis/sanity'
import { useCustomerType } from '../../modules/apis/student/products'
import { usePromotionCode } from '../../modules/apis/student/promotion'
import { useCurrentUser } from '../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../modules/hooks/common/useIsWebview'
import useReturnFullUrl from '../../modules/hooks/webview/common/useReturnFullUrl'
import useTranslation from '../../modules/i18n/useTranslation'
import { ICON_X_WHITE } from '../../modules/svg/svg'
import { getDday, isB2BHRGroup, isMulticampus } from '../../modules/utils'
import useGnbStore from '../../store/useGnbStore'
import { D_DAY_TYPE, DdayCounter } from './DdayCounter'

export interface IBanner {
  description: string
  case: Case[]
}

export interface Case {
  type: string
  id: string
  enable_url: string[]
  d_day_counter_enable: boolean
  d_day_target_date: string
  web_link: string
  app_link: string
  desktop: Desktop
  mobile: Desktop
  background_color: string
}

export interface Desktop {
  ko: Text[]
  en: Text[]
}

export interface Text {
  text: string
  color: string
}

export const enum UserType {
  //B2C
  NotLogin = 'NOT_LOGIN',
  LoginNewUser24HrPaidUser = 'LOGIN_NEW_USER_24HR_PAID_USER',
  LoginNewUser24HrNotPaidUser = 'LOGIN_NEW_USER_24HR_NOT_PAID_USER',
  ExistingUserNotPaid = 'EXISTING_USER_NOT_PAID',
  ExistingUserPaid = 'EXISTING_USER_PAID',
  PromotionUser = 'PROMOTION_TARGETED_USER',
  // B2B
  B2BTab = 'B2B_TAB',
  B2BHRInChargeGroup = 'B2B_HR_IN_CHARGE_GROUP',
  B2BCodeGroupCompanyTarget = 'B2B_COMPANY_CODE_GROUP_TARGET',
  B2BCodeSingleCompanyTarget = 'B2B_COMPANY_CODE_SINGLE_TARGET',
}
interface TransitionOptions {
  appScreenName?: string
  appSendData?: Record<string, unknown>
}

const BandBanner = ({ isMobile, isUserLoggedIn, isPortal, handleOnClickGnb, onCloseGnb, bannerData }) => {
  const isWebview = useIsWebview()
  const { locale } = useTranslation()
  const D_DAY_COUNTER = 'D_DAY_COUNTER'
  const getFullUrl = useReturnFullUrl()

  const hasPromotionData = bannerData?.promotion_labels?.length > 0
  const { data: promotionData, isLoading: isPromotionIdLoading } = usePromotionCode({
    enabled: hasPromotionData,
  })
  const applicablePromotionId = promotionData?.promotion_id

  if (!bannerData) return null

  const link = isWebview ? bannerData?.app_link : bannerData?.web_link

  if (hasPromotionData && isPromotionIdLoading) return null

  const displayedBannerData = applicablePromotionId
    ? bannerData?.promotion_labels?.find((label) => label.promotionId == applicablePromotionId)
    : null

  // 프로모션 ID와 매칭되는 배너가 없으면 띠배너를 보여주지 않음
  if (!displayedBannerData) {
    return null
  }

  // promotion API의 displayed_end_time 사용 (Sanity d_day 대신)
  const dueDate = promotionData?.displayed_end_time ? new Date(promotionData.displayed_end_time).getTime() : null
  const dday = dueDate ? getDday(new Date(dueDate)) : null

  const backgroundColor = displayedBannerData?.background_color || bannerData?.background_color
  const labelData = isMobile ? displayedBannerData?.mobile : displayedBannerData?.desktop

  const localeLabel = labelData?.[locale] || labelData?.en || []

  return (
    <div
      style={{ backgroundColor: backgroundColor }}
      className={classNames(
        'relative flex w-full items-center justify-center Body2-14Bd sm:Body1-16Bd',
        isMobile && isUserLoggedIn && 'px-[20px]',
        isPortal ? 'h-[40px] sm:h-[40px]' : 'h-[40px] sm:h-[40px]'
      )}
    >
      <div
        onClick={debounce(() => {
          handleOnClickGnb(
            link,
            {
              appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
              appSendData: {
                url: getFullUrl(`${link}?app=true`),
              },
            },
            bannerData?.id
          )
        }, 500)}
        className="flex cursor-pointer items-center"
      >
        <div className="flex-row flex whitespace-nowrap gap-x-[4px]">
          {localeLabel?.map((label, i) => {
            const color = label?.color || '000000'
            const formattedColor = color.startsWith('#') ? color : '#' + color

            //D_DAY_COUNTER
            if (label?.text == D_DAY_COUNTER) {
              // sanity의 d_day_counter_enable flag 체크
              const counterEnabled = displayedBannerData?.d_day_counter_enable
              if (counterEnabled && promotionData?.displayed_end_time && dueDate) {
                return (
                  <div key={i} className="" style={{ color: formattedColor }}>
                    <DdayCounter
                      padReplaceD={true}
                      initialDay={new Date()}
                      targetDay={new Date(dueDate)}
                      types={dday <= 1 ? [D_DAY_TYPE.HOUR, D_DAY_TYPE.MIN, D_DAY_TYPE.SEC] : [D_DAY_TYPE.DAY]}
                      classNameTotalContainer="flex items-center"
                      classNameDigitContainer="flex items-center Body2-14Bd"
                      classNameDigit=""
                      seperator={<div className="mx-[1px] flex flex-col justify-center Body2-14Bd">:</div>}
                      text={<span className="Body2-14Bd"></span>}
                    />
                  </div>
                )
              } else {
                // d_day_counter_enable이 false이면 빈 span 반환 (null 대신)
                return <span key={i}></span>
              }
            }

            return (
              <span className="Body2-14Bd" style={{ color: formattedColor }} key={i}>
                {label?.text}
              </span>
            )
          })}
        </div>
        {/* 화살표는 모바일이 아닐 때만 보여짐 */}
        <QIcon_chevron_bold_right width={16} height={16} className="ml-[4px] hidden text-white md:block" />
      </div>
      <img
        src={ICON_X_WHITE}
        onClick={() => onCloseGnb()}
        alt="close-button"
        className={classNames(
          { 'mr-[20px]': isMobile },
          { 'mr-[94px]': !isMobile },
          'absolute right-[0px] cursor-pointer'
        )}
      />
    </div>
  )
}

const BandBannerContainer = () => {
  const router = useAppRouter()
  const { currentUser } = useCurrentUser()
  const isMobile = useIsMobile()
  const isWebview = useIsWebview()
  const isUserLoggedIn = currentUser !== null
  const isPortal = useMemo(() => router.asPath.includes('/portal/'), [router.asPath])
  const { sendClickEvent } = useEventTracking()

  const { data: customerType } = useCustomerType({ enabled: !!currentUser })
  const isB2CEligible = customerType?.isB2CEligible
  // FIXME
  const { data: bandBannerData } = useBandBannerData()

  // Get promotion data for period checking
  const hasPromotionData = bandBannerData?.promotion_labels?.length > 0
  const { data: promotionData } = usePromotionCode({
    enabled: hasPromotionData,
  })

  const [hasClickedClose, setHasClickedClose] = useState(false)
  const getFullUrl = useReturnFullUrl()

  const gnbShow = useGnbStore((state) => state.show)
  const gnbClose = useGnbStore((state) => state.close)

  const notEnableUrl = !bandBannerData?.enable_url?.some((path) => router.asPath.includes(path))

  // 배너 데이터 / 닫기 버튼 클릭 / 허용되지 않은 URL
  const notShowBandBannerCondition = hasClickedClose || !bandBannerData || notEnableUrl

  const handleOnClickGnb = (url: string, option: TransitionOptions, id = '') => {
    sendClickEvent({ button_name: 'band_banner' })

    if (isWebview) {
      if (url.includes('http')) {
        router.linking(url)
      } else {
        // App -> StackScreen
        router.push('', {
          appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
          appSendData: { url: getFullUrl(url) },
        })
      }
    } else {
      if (url.includes('http')) {
        window.open(url, '_blank')
      } else {
        // Web -> useRouter router.push
        router.push(url, {
          appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
          appSendData: { url: getFullUrl(url) },
        })
      }
    }
  }

  const onCloseGnb = useCallback((gnbId: string, dayCnt: number) => {
    setHasClickedClose(true)
    gnbClose()
  }, [])

  useEffect(() => {
    if (isMulticampus(currentUser) || isB2BHRGroup(currentUser)) return

    if (notShowBandBannerCondition) {
      gnbClose()
    } else {
      gnbShow()
    }
  }, [bandBannerData])

  if (notShowBandBannerCondition) return <></>
  if (isMulticampus(currentUser) || isB2BHRGroup(currentUser)) return
  if (currentUser && isB2CEligible === false) return

  return (
    <div className={classNames('flex items-center justify-center w-full')}>
      <BandBanner
        isMobile={isMobile}
        isUserLoggedIn={isUserLoggedIn}
        isPortal={isPortal}
        handleOnClickGnb={handleOnClickGnb}
        onCloseGnb={onCloseGnb}
        bannerData={bandBannerData}
      />
    </div>
  )
}

export default React.memo(BandBannerContainer)
